import { apiRequest } from './client.js';

/**
 * Vendor branding profile (M17) — which branding types and sub-types a vendor
 * may be given work for, per financial year. The financial year is a
 * t604_planning_cycle, the same one the budget module plans against.
 */

export interface PlanningCycle {
  readonly planningCycleId: string;
  readonly cycleCode: string;
  readonly cycleName: string;
  readonly startYear: number;
  /** True for the cycle the tenant's business date falls in (server-decided). */
  readonly isCurrent: boolean;
}

/** The financial year every FY list box opens on: the tenant's current one. */
export function currentCycleId(
  cycles: ReadonlyArray<{ readonly planningCycleId: string; readonly isCurrent: boolean }>,
): string {
  return cycles.find((c) => c.isCurrent)?.planningCycleId ?? '';
}

export interface TaxonomyRow {
  readonly brandingTypeId: string;
  readonly typeCode: string;
  readonly typeName: string;
  readonly brandingItemId: string | null;
  readonly itemCode: string | null;
  readonly itemName: string | null;
}

export interface ProfileScopeRow {
  readonly brandingTypeId: string;
  readonly typeCode: string;
  readonly typeName: string;
  readonly brandingItemId: string | null;
  readonly itemCode: string | null;
  readonly itemName: string | null;
}

/** A forward / reverse charge choice, as the GST option catalogue offers it. */
export interface ChargeBasisOption {
  readonly optionCode: string;
  readonly optionLabel: string;
}

export interface VendorBrandingProfile {
  readonly vendorBrandingProfileId: string | null;
  readonly vendorId: string;
  readonly planningCycleId: string;
  readonly cycleCode: string | null;
  readonly remarks: string | null;
  /** FCM / RCM for this vendor in this year; null = not yet recorded. */
  readonly taxChargeBasisCode: string | null;
  readonly chargeBasisOptions: readonly ChargeBasisOption[];
  readonly version: number;
  readonly scope: readonly ProfileScopeRow[];
}

export interface SaveProfileInput {
  readonly vendorId: string;
  readonly planningCycleId: string;
  readonly remarks?: string;
  readonly taxChargeBasisCode: string;
  readonly scope: ReadonlyArray<{
    readonly brandingTypeId: string;
    readonly brandingItemId?: string;
  }>;
}

/** A vendor that holds a branding profile for a year, with the year's charge basis. */
export interface ProfiledVendor {
  readonly vendorId: string;
  readonly vendorCode: string;
  readonly vendorName: string;
  /** Null = FCM/RCM not recorded for the year: no work order can be saved yet. */
  readonly taxChargeBasisCode: string | null;
}

type Tok = string | null | undefined;

const BASE = '/v1/m17/vendor-branding-profiles';

export function listPlanningCycles(token?: Tok): Promise<readonly PlanningCycle[]> {
  return apiRequest<readonly PlanningCycle[]>(`${BASE}/cycles`, { token });
}

/** Vendors that hold a branding profile for that year — the only ones who may be given work in it. */
export function listProfiledVendors(
  planningCycleId: string,
  token?: Tok,
): Promise<readonly ProfiledVendor[]> {
  return apiRequest(
    `${BASE}/vendors?planningCycleId=${encodeURIComponent(planningCycleId)}`,
    { token },
  );
}

export function listBrandingTaxonomy(token?: Tok): Promise<readonly TaxonomyRow[]> {
  return apiRequest<readonly TaxonomyRow[]>(`${BASE}/taxonomy`, { token });
}

export function getVendorBrandingProfile(
  vendorId: string,
  planningCycleId: string,
  token?: Tok,
): Promise<VendorBrandingProfile> {
  const qs = `?vendorId=${encodeURIComponent(vendorId)}&planningCycleId=${encodeURIComponent(planningCycleId)}`;
  return apiRequest<VendorBrandingProfile>(`${BASE}${qs}`, { token });
}

export function saveVendorBrandingProfile(
  body: SaveProfileInput,
  token?: Tok,
): Promise<VendorBrandingProfile> {
  return apiRequest<VendorBrandingProfile>(BASE, { method: 'PUT', token, body });
}
